import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LayoutDashboard, Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useState } from 'react'
import { useParams } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { Layout } from '@/components/Layout'
import { AddEditAccountSlideout } from '@/pages/Admin/Accounts'
import {
  deleteOrganizationAccount,
  getOrganizationAccounts,
} from '@/services/organization.service'
import { updateUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const OrganizationAccounts = observer(() => {
  const { organizationId } = useParams()
  const { user, setUser } = useContext(UserStoreContext)

  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({ pageSize: 20, pageIndex: 0 })
  const [showSlideout, setShowSlideout] = useState(false)
  const [editAccount, setEditAccount] = useState(null)

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()

  const accountsQueryKey = ['organization-accounts', organizationId, sorting, pagination]
  const { data: accounts, isPending } = useQuery({
    queryKey: accountsQueryKey,
    queryFn: () => getOrganizationAccounts({ organizationId, sorting, pagination }),
    placeholderData: keepPreviousData,
    enabled: !!organizationId,
  })

  const { isPending: pendingDelete, mutate: deleteAccount } = useMutation({
    mutationFn: (id) => deleteOrganizationAccount({ organizationId, id }),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Facility deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['organization-accounts', organizationId] })
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId, 'summary'] })
      // The switcher / admin lists may include this account too.
      queryClient.invalidateQueries({ queryKey: ['all-entities'] })
      queryClient.invalidateQueries({ queryKey: ['entities'] })
    },
  })

  const openEdit = (account) => {
    setEditAccount(account)
    setShowSlideout(true)
  }

  // Switch the user's context into the facility and land on its dashboard.
  // PrivateRoute's reaction on activeEntity handles the navigation.
  const openFacility = async (entityId) => {
    const response = await updateUser({
      id: user.id,
      activeEntity: entityId,
      activeOrganization: null,
    })
    setUser(response)
  }

  return (
    <Layout breadcrumbs={['Organization', 'Facilities']}>
      <Layout.Heading title="Facilities" className="mb-5">
        <Button onClick={() => setShowSlideout(true)}>Add Facility</Button>
      </Layout.Heading>

      <DataTable
        columns={[
          {
            accessorKey: 'name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <button
                type="button"
                className="text-action cursor-pointer text-left hover:underline"
                onClick={() => openEdit(row.original)}
              >
                {row.original.name}
              </button>
            ),
          },
          {
            id: 'dashboard',
            size: 140,
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                variant="outline"
                size="sm"
                onClick={() => openFacility(row.original.id)}
              >
                <LayoutDashboard className="mr-1 h-4 w-4" />
                Dashboard
              </Button>
            ),
          },
          {
            id: 'edit',
            size: 80,
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                variant="outline"
                size="sm"
                onClick={() => openEdit(row.original)}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
            ),
          },
          {
            id: 'delete',
            size: 80,
            cell: ({ row }) => (
              <ConfirmButton
                confirmContent={`Permanently delete "${row.original.name}" and all of its data (users, courses, certificates, records)? This cannot be undone.`}
                confirmTitle="Delete Facility"
                className="place-self-end"
                variant="destructive"
                size="sm"
                loading={pendingDelete}
                onClick={() => deleteAccount(row.original.id)}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Delete account</span>
              </ConfirmButton>
            ),
          },
        ]}
        data={accounts?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={accounts?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {showSlideout && (
        <AddEditAccountSlideout
          editAccount={editAccount}
          organizationId={organizationId}
          resetAndClose={() => {
            setShowSlideout(false)
            setEditAccount(null)
          }}
        />
      )}
    </Layout>
  )
})

export default OrganizationAccounts
